// imports
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// local imports
import CustomInput from '../../../CustomInput';
import ColorPickerPopover from '../../../ColorPickerPopover';
import {
  defaultBorderColor,
  GeoJsonAdditionalLayer,
  MVTAdditionalLayer,
} from '../../../../../configs/additionalLayers';
import { mapActions } from '../../../../../store';

interface AdditionalLayerBorderControlsProps {
  layer: MVTAdditionalLayer | GeoJsonAdditionalLayer;
}

const minBorderWidth = 0;
const maxBorderWidth = 99;
/**
 * AdditionalLayerBorderControls
 *
 * Renders the border color and width controls for the vector data with geometryType 'line'
 *
 * @param layer - The additionalLayer object
 * @returns {JSX.Element} The rendered border color and width controls.
 */
const BorderContainer = styled(Box)`
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: center;
`;

export default function AdditionalLayerBorderControls({
  layer,
}: AdditionalLayerBorderControlsProps) {
  const dispatch = useDispatch();

  const [borderColorValue, setBorderColorValue] = useState<string>(
    layer.borderColor || defaultBorderColor
  );

  const handleBorderWidthChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newBorderWidth = event.target.value;
    if (newBorderWidth === '') {
      dispatch(mapActions.setBorderWidth({ id: layer.id, borderWidth: '' }));
    } else {
      const borderWidthNum = Number(newBorderWidth);
      if (
        !isNaN(borderWidthNum) &&
        borderWidthNum >= minBorderWidth &&
        borderWidthNum <= maxBorderWidth
      ) {
        dispatch(
          mapActions.setBorderWidth({
            id: layer.id,
            borderWidth: borderWidthNum,
          })
        );
      }
    }
  };

  useEffect(() => {
    dispatch(
      mapActions.setBorderColor({
        id: layer.id,
        borderColor: borderColorValue,
      })
    );
  }, [borderColorValue, layer.id, dispatch]);

  return (
    <Box id="border-container" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#555', letterSpacing: '0.05em' }}>
        Border
      </Typography>
      <BorderContainer>
        <ColorPickerPopover
          color={borderColorValue}
          onChange={setBorderColorValue}
          data-testid="border-color-picker"
          aria-label="Border color"
        />
        <CustomInput
          value={layer.borderWidth ?? ''}
          onChange={handleBorderWidthChange}
          width="80px"
          suffix="px"
          type="number"
          min={minBorderWidth.toString()}
          max={maxBorderWidth.toString()}
          data-testid="border-width-picker"
          aria-label="Border width in pixels"
        />
      </BorderContainer>
    </Box>
  );
}
